import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AdminService } from 'src/app/shared/services/admin.service';

interface SelfAssessment {
  assessment_id?: number;
  name: string;
  quiz_ids: string;
  score_graph?: string;
  formula: string;
  passing_score: number;
}

@Component({
  selector: 'app-manage-self-assessments',
  templateUrl: './manage-self-assessments.component.html',
  styleUrls: ['./manage-self-assessments.component.scss'],
  providers: [MessageService]
})
export class ManageSelfAssessmentsComponent implements OnInit {
  selfAssessments: SelfAssessment[] = [];
  showDialog = false;
  showDeleteDialog = false;
  dialogHeader = 'Add New Assessment';
  assessmentForm: FormGroup;
  selectedAssessment: SelfAssessment | null = null;
  incorrect_questions = '{{incorrect_questions}}';
  correct_questions = '{{correct_questions}}';
  selectedFileName: string = '';
  previewUrl: string | null = null;
  loader = false;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private adminService: AdminService
  ) {
    this.assessmentForm = this.fb.group({
      quizIds: ['', [Validators.required, Validators.pattern(/^[\d,\s]+$/)]],
      formula: ['', [Validators.required]],
      scoreGraph: [null],
      passingScore: [70, [Validators.required, Validators.min(0), Validators.max(100)]]
    });
  }

  ngOnInit(): void {
    this.loadAssessments();
  }

  loadAssessments(): void {
    this.loader = true;
    this.adminService.getSelfAssessments().subscribe((res: any) => {
      this.loader = false;
      if (res.data.status) {
        this.selfAssessments = res.data.selfAssessments;
      }
    })
  }

  editAssessment(assessment: SelfAssessment): void {
    this.dialogHeader = 'Edit Assessment';
    this.selectedAssessment = assessment;
    // Convert quiz_ids array to comma-separated string if it's an array
    const quizIdsString = Array.isArray(JSON.parse(assessment.quiz_ids)) 
      ? JSON.parse(assessment.quiz_ids).join(',') 
      : '';
    this.assessmentForm.patchValue({
      quizIds: quizIdsString,
      formula: assessment.formula,
      passingScore: assessment.passing_score
    });
    this.previewUrl = assessment.score_graph;
    this.showDialog = true;
  }

  hideDialog(): void {
    this.showDialog = false;
    this.assessmentForm.reset();
  }

  onFileSelect(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      if (file.size > 1000000) { // 1MB limit
        // Handle file size error
        return;
      }
      this.selectedFileName = file.name;
      this.previewUrl = URL.createObjectURL(file);
      // Update form control with the file
      this.assessmentForm.patchValue({
        scoreGraph: file
      });
    }
  }

  removeImage() {
    this.selectedFileName = '';
    this.previewUrl = null;
    this.assessmentForm.patchValue({
      scoreGraph: null
    });
  }

  saveAssessment(): void {
    if (this.assessmentForm.valid) {
      const data = this.assessmentForm.value;
      const formData = new FormData();
      
      // Convert quizIds string to array
      const quizIdsArray = data.quizIds.split(',').map((id: string) => id.trim()).filter((id: string) => id !== '');
      formData.append('quizIds', JSON.stringify(quizIdsArray));
      
      formData.append('formula', data.formula);
      formData.append('scoreGraph', data.scoreGraph);
      formData.append('passingScore', data.passingScore.toString());
      if (!data.scoreGraph && this.previewUrl) {
        formData.append('alreadyUploaded', this.selectedAssessment.score_graph);
      }
      formData.append('assessmentId', this.selectedAssessment?.assessment_id.toString());

      this.adminService.updateSelfAssessment(formData).subscribe((res: any) => {
        if (res.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Self Assessment updated successfully`
          });
        }
        this.loadAssessments();
      })
      this.hideDialog();
    }
  }

  deleteAssessment(assessment: SelfAssessment): void {
    this.selectedAssessment = assessment;
    this.showDeleteDialog = true;
  }

  hideDeleteDialog(): void {
    this.showDeleteDialog = false;
    this.selectedAssessment = null;
  }

  confirmDelete(): void {
    if (this.selectedAssessment) {
      this.selfAssessments = this.selfAssessments.filter(a => a.assessment_id !== this.selectedAssessment?.assessment_id);
      this.hideDeleteDialog();
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Assessment deleted successfully'
      });
    }
  }
}
